import { useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

export function TwoFactorCard() {
  const [enabled, setEnabled] = useState(null); // null = loading
  const [mode, setMode] = useState("idle"); // idle | enrolling | disabling
  const [qr, setQr] = useState("");
  const [manualKey, setManualKey] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get("/auth/2fa/status")
      .then(({ data }) => setEnabled(!!data.enabled))
      .catch(() => setEnabled(false));
  }, []);

  const startEnroll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup/start");
      setQr(data.qr_data_uri);
      setManualKey(data.manual_key);
      setMode("enrolling");
      setCode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not start setup."));
    } finally {
      setBusy(false);
    }
  };

  const confirmEnroll = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) return toast.error("Enter the current 6-digit code.");
    setBusy(true);
    try {
      await api.post("/auth/2fa/setup/confirm", { code: digits });
      setEnabled(true);
      setMode("idle");
      setQr("");
      setManualKey("");
      toast.success("Two-factor authentication is now on.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "That code didn't match."));
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    const digits = code.replace(/\D/g, "");
    if (digits.length !== 6) return toast.error("Enter a current 6-digit code to turn it off.");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: digits });
      setEnabled(false);
      setMode("idle");
      setCode("");
      toast.success("Two-factor authentication turned off.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Could not disable 2FA."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-[#E5E5E5] p-8" data-testid="twofactor-card">
      <div className="flex items-center gap-2 gv-label">
        {enabled ? <ShieldCheck className="w-4 h-4 text-green-600" /> : <ShieldAlert className="w-4 h-4 text-[#C5A059]" />}
        Two-factor authentication
      </div>
      <p className="text-sm text-[#5C6670] mt-2 mb-6">
        Protect the studio with Google Authenticator. Once on, sign-in needs your password <em>and</em> a 6-digit code from your phone.
      </p>

      {enabled === null && <p className="text-sm text-[#B8BCC2]">Loading…</p>}

      {enabled === true && mode !== "disabling" && (
        <div className="flex items-center justify-between" data-testid="twofactor-on">
          <span className="text-sm text-green-700 font-medium">✓ Enabled — your account is protected.</span>
          <button
            type="button"
            onClick={() => { setMode("disabling"); setCode(""); }}
            className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-red-600 border border-[#E5E5E5] hover:border-red-300 px-3 py-1.5"
            data-testid="twofactor-disable-start"
          >
            Turn off
          </button>
        </div>
      )}

      {enabled === true && mode === "disabling" && (
        <div className="space-y-4" data-testid="twofactor-disable">
          <p className="text-sm text-[#5C6670]">Enter a current authenticator code to turn 2FA off.</p>
          <input
            inputMode="numeric" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="gv-input text-center text-xl tracking-[0.4em] max-w-[12rem]" placeholder="••••••"
            data-testid="twofactor-disable-code"
          />
          <div className="flex gap-3">
            <button type="button" onClick={disable} disabled={busy} className="gv-btn-gold" data-testid="twofactor-disable-confirm">
              {busy ? "Working…" : "Confirm turn off"}
            </button>
            <button type="button" onClick={() => setMode("idle")} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059]">
              Cancel
            </button>
          </div>
        </div>
      )}

      {enabled === false && mode === "idle" && (
        <button type="button" onClick={startEnroll} disabled={busy} className="gv-btn-gold" data-testid="twofactor-enable-start">
          {busy ? "Preparing…" : "Enable 2FA"}
        </button>
      )}

      {enabled === false && mode === "enrolling" && (
        <div className="space-y-5" data-testid="twofactor-enroll">
          <div className="flex flex-col sm:flex-row gap-6 items-start">
            {qr && <img src={qr} alt="Scan with Google Authenticator" className="w-44 h-44 border border-[#E5E5E5]" data-testid="twofactor-qr" />}
            <div className="text-sm text-[#5C6670] space-y-2">
              <p>1. Open Google Authenticator and scan this QR code.</p>
              <p>2. Or add manually with this key:</p>
              <code className="block bg-[#FAFAF8] border border-[#E5E5E5] px-3 py-2 font-mono text-xs break-all" data-testid="twofactor-manual-key">{manualKey}</code>
              <p>3. Enter the 6-digit code it shows to finish.</p>
            </div>
          </div>
          <input
            inputMode="numeric" maxLength={6} value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
            className="gv-input text-center text-xl tracking-[0.4em] max-w-[12rem]" placeholder="••••••"
            data-testid="twofactor-confirm-code"
          />
          <div className="flex gap-3">
            <button type="button" onClick={confirmEnroll} disabled={busy} className="gv-btn-gold" data-testid="twofactor-confirm">
              {busy ? "Verifying…" : "Verify & enable"}
            </button>
            <button type="button" onClick={() => { setMode("idle"); setQr(""); }} className="text-xs tracking-[0.2em] uppercase text-[#5C6670] hover:text-[#C5A059]">
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
